// @ts-strict-ignore
/* eslint-disable no-continue */
import type {
  Action, DataRoute, PublicId, Threshold,
} from 'flow/store/engine/types';
import {
  regenerateLinkedSnippetsPublicIds,
  getSnippetChildActions,
  resolveDataRouteSourceReferences,
  resolveThresholdParentReferences,
  createSnippetVersion,
  sortSnippetInTopologicalOrder,
} from '../../snippets';
import type { SnippetVersion, WFElement, WFElementOperationMap } from '../../types';
import { isActionOfType, indexActionsByPublicId, removeNonDeterministicActionFields } from 'flow/service/actions';
import {
  isChildOfSnippetAction,
  getRemoteActionPublicId,
  isExternalActionType,
  isExternalSnippetChildAction,
  isSourceActionMergedIntoTargetAction,
  supportsActionMerging,
  appendSnippetParent,
  removeSnippetParentFromChildContainer,
  actionRequiresGateRule,
  createSnippetContainerContext,
  getGateRuleId,
} from 'flow/service/actions/snippets';
import { getDataRoutesForActions, indexDataRoutesByPublicId } from 'flow/service/dataRoutes';
import { getThresholdsForActions, indexThresholdsByPublicId } from 'flow/service/thresholds';
import { ActionType, booleanRuleStrings } from 'flow/store/engine/constants';
import { createSnippetGateThreshold } from 'flow/store/engine/update/thresholds';
import { getEquivalencyKeyForAction } from '../../collisions/equivalency';
import { getCollisionsWithinPrimitives } from '../../collisions';
import { deleteProperty } from 'dot-prop';
import { modifyRelevantDataFromActionRemoval } from 'flow/store/engine/update/actions';
import { addThresholdToBooleanRule } from 'flow/store/engine/update/rules/rules';
import { areEqual, areDifferent } from 'flow/service/util';
import { buildRemoteToLocalPublicIdMap } from './remoteIdRemap';


/**
 * Creates a "local next" snippet for a given current and next versions.
 * Terminology:
 * - remote current - the remote snippet version (in the library) that maps to an existing local version.
 * - local current - the local snippet version that maps to a remote snippet version from the library
 *    - local differs from remote in that all of its ids are different, and every child action has awareness of its snippet container
 *
 * - remote next - the next remote version (in the library) that the local current should be upgraded to.
 * - local next - the next safe version to use. the next local will have:
 *    - different ids from remote next
 *    - any element that exists in the local current will retain its id. this is crucial, as it lets us track
 *      operations (added/modified/removed/noop) on them across current and next local versions.
 */
export function buildLocalNextSnippet(args: {
  localCurrentSnippetData: SnippetVersion['data'];
  remoteCurrentSnippet: SnippetVersion;
  localSnippetActionPublicId: PublicId;
  remoteNextSnippet: SnippetVersion;
}): SnippetVersion {
  /**
   * we do not track remote threshold or data route ids, so this function infers them by mapping
   * ids across the local current and local remote versions:
   * think of current as v1 and next as v2 (we dont use those names as we could have any combination
   * of version numbers)
   *
   *  REMOTE                LOCAL
   *  (in library)          (in workflow)
   * ----------            ----------
   * | current |           | current |
   * ----------            ----------
   *     |                     |
   *     |                     |
   *     v                     v
   * ---------             ---------
   * | next  |             | next  |   <=== this is what we build in this function
   * ---------             ---------
   *
   * here's how it's done:
   * - we build a map from local current to local remote. these 2 versions have the same
   *   structure (as we dont allow changes in local snippets without unlinking), making the
   *   mapping possible. **this is a crucial detail**.
   *    - actions, thresholds and dataroutes have their own logic for building this map
   * - we know that publicIds persist across versions. this means that ids in remote current
   *   and remote next are preserved, so any local current that maps to a remote current id
   *   also maps to an id in remote next, assuming the element was not removed in the remote
   *   next version.
   *    - if an id is missing in remote next, we can assume the element was deleted in remote next.
   *    - if an id is missing in local current, we can assume the element was added in remote next.
   *    - this function does not care about these operations. it just builds a snippet version.
   * - to compute the local next, we first need to prepare the remote next for insertion:
   *   - regenerate any ids for new elements, preserving any local ids from the current local version
   *   - run the snippet with regenerated ids through a preparation step to maintain & transfer, or
   *     create, any data that needs to be, such as container contexts, and gate rules.
   */
  const {
    localCurrentSnippetData, localSnippetActionPublicId, remoteCurrentSnippet, remoteNextSnippet,
  } = args;


  // id map mapping a remote id to its local id
  const currentRemoteToLocalIds = buildRemoteToLocalPublicIdMap({
    localSnippetData: localCurrentSnippetData,
    localSnippetActionPublicId,
    remoteCurrentSnippet,
    remoteNextSnippet,
  });

  // regenerate all ids in the incoming snippet but use local (resulting) ids for anything that
  // already exists in the current local version
  const {
    localToRemotePublicIds: nextDraftToRemoteIds, snippet: remappedRemoteNextSnippet,
  } = regenerateLinkedSnippetsPublicIds({
    snippet: remoteNextSnippet,
    remoteToLocalPublicIdOverrides: currentRemoteToLocalIds,
  });

  const localNextSnippet = prepareLinkedSnippetForUpgrade({
    snippet: remappedRemoteNextSnippet,
    localToRemotePublicIds: nextDraftToRemoteIds,
    parentSnippetActionPublicId: localSnippetActionPublicId,
    localCurrentSnippetData,
  });

  return localNextSnippet;
}


/**
 * Applies a snippet upgrade onto a workflow data graph and returns the result
 */
export function applySnippetVersionUpgrade(args: {
  workflow: {
    actions: Record<PublicId, Action>;
    thresholds: Record<PublicId, Threshold>;
    dataRoutes: Record<PublicId, DataRoute>;
  };
  snippet: SnippetVersion;
  localSnippetActionPublicId: PublicId;
}): {
    actions: Record<PublicId, Action>;
    thresholds: Record<PublicId, Threshold>;
    dataRoutes: Record<PublicId, DataRoute>;
  } {
  /**
     * runs through all of the changes between the incoming snippet and the snippet actions already
     * present in the workflow to determine what changed and how.
     *
     * a snippet action can be merged with an equivalent workflow action during an insertion (the
     * added action is equivalent) or a modification (the existing action has been modified to now be
     * equivalent). when that happens, we favor the the wf action and do not insert (or remove) the
     * snippet action from the result, and we update the wf action to contain the container context
     * entries for the incoming snippet action.
     *
     * additionally, any threshold or dataRoute that pointed to it must be resolved to
     * replace that pointer with the wf action that it was merged into - these could be owned by a
     * snippet or workflow action.
     *
     * thresholds and dataRoutes are *not* mergeable.
     *
     * an action can also be *un*merged if it was previously merged with a workflow action but the next
     * version of the action is no longer equivalent. workflow actions should also be unmerged if the
     * next version of the snippet no longer contains the action that it was once merged with.
     *
     * this function runs 3 main steps:
     * 1. compute the decisions - what to merge/unmerge, and what changed & how across versions.
     * 2. unmerge - edit the once-merged actions and split them into 2 when applicable
     * 3. apply delta map - add, update, or remove actions based on the incoming snippet
     */
  const {
    localSnippetActionPublicId, snippet, workflow,
  } = args;


  // clone the workflow data to mutate. this is our result
  const nextWorkflowGraphData = structuredClone({
    actions: workflow.actions,
    thresholds: workflow.thresholds,
    dataRoutes: workflow.dataRoutes,
  });

  // ==== Step 1: compute decisions ====
  const wfSnippetChildActions = getSnippetChildActions({
    actions: workflow.actions,
    snippetParentActionPublicId: localSnippetActionPublicId,
  });

  const actionDeltaMap = buildActionsDeltaMap({
    localSnippetActionPublicId,
    snippetActions: snippet.data.actions,
    workflowActions: wfSnippetChildActions,
  });

  // map of actions (ids) to merge
  const externalActionMergeMap = computeExternalActionMergeMap({
    workflow,
    snippet,
    snippetActionPublicId: localSnippetActionPublicId,
  });

  // map of action (ids) to unmerge
  const externalActionUnmergeMap = computeExternalActionUnmergeMap({
    parentSnippetActionPublicId: localSnippetActionPublicId,
    snippet,
    workflow,
  });


  // ==== Step 2: unmerge ====
  /**
   * for every snippet action to unmerge out, we need to:
   *  - update the workflow action and remove the parent snippet entry from it
   */
  for (const workflowActionId of externalActionUnmergeMap.keys()) {
    // remove the entry for the remote action and update graph actions
    const wfAction = nextWorkflowGraphData.actions[workflowActionId];
    nextWorkflowGraphData.actions[workflowActionId] = removeSnippetParentFromChildContainer(
      wfAction,
      localSnippetActionPublicId,
    );
  }


  // ==== Step 3: apply delta map ====
  for (const addedOrModifiedActionId of [...actionDeltaMap.added, ...actionDeltaMap.modified]) {
    const snippetAction = snippet.data.actions[addedOrModifiedActionId];
    // an action will only be either added or modified
    const isModified = actionDeltaMap.modified.has(addedOrModifiedActionId);
    const toMerge = externalActionMergeMap.has(snippetAction.publicId);

    if (!toMerge) {
      /**
       * simply add it if no merging needed.
       * once-merged, but now unmerged actions will appear as 'added' since they dont exist in
       * the wf yet
       */
      nextWorkflowGraphData.actions[snippetAction.publicId] = snippetAction;
    } else {
      const equivalentWorkflowActionPublicId = externalActionMergeMap.get(snippetAction.publicId);
      const equivalentWorkflowAction = workflow.actions[equivalentWorkflowActionPublicId];

      const snippetActionRemoteId = getRemoteActionPublicId({
        action: snippetAction,
        snippetParentActionPublicId: localSnippetActionPublicId,
      });

      const updatedAction = appendSnippetParent({
        action: equivalentWorkflowAction,
        parentSnippetActionPublicId: localSnippetActionPublicId,
        remoteActionPublicId: snippetActionRemoteId,
      });

      nextWorkflowGraphData.actions[equivalentWorkflowActionPublicId] = updatedAction;

      // if modified, we need to remove the original action as it's now merged
      if (isModified) {
        deleteProperty(nextWorkflowGraphData.actions, addedOrModifiedActionId);
        /**
         * No need to clean up relevant data for action update:
         * - thresholds are cleaned up in the threshold delta map
         * - data routes are cleaned up in the data route delta map
         * If in the case, we need to clean up additional data outside of those two cases for action update,
         * we can use the modifyRelevantDataFromActionUpdate function with public id as path
         */
      }
    }
  }

  // manually remove all actions that are no longer present in the snippet
  for (const removedActionId of actionDeltaMap.removed) {
    // skip this WF action if it was unmerged above (kept in workflow, just unlinked from snippet)
    if (externalActionUnmergeMap.has(removedActionId)) continue;

    const removedAction = nextWorkflowGraphData.actions[removedActionId];
    if (!removedAction) continue;
    deleteProperty(nextWorkflowGraphData.actions, removedActionId);

    // note - this function runs the updates in-place
    // we remove thresholds, data routes, and dependent actions
    // related to the removed action from nextWorkflowGraphData
    modifyRelevantDataFromActionRemoval({
      originalTargetValue: removedAction,
      workflowData: nextWorkflowGraphData,
    });
  }


  // ==== Thresholds ====
  /**
   * resolve all snippet thresholds to update their references to point
   * to any merged action.
   * there's no need to resolve unmerged actions because the incoming snippet version
   * has thresholds that already point to that action
   */
  const resolvedSnippetThresholds = resolveThresholdParentReferences({
    actionIdMap: externalActionMergeMap,
    thresholds: Object.values(snippet.data.thresholds),
  });

  /**
   * a workflow threshold might point to a snippet action that gets merged during the update,
   * so those are resolved as well
   */
  const resolvedWorkflowThresholds = resolveThresholdParentReferences({
    actionIdMap: externalActionMergeMap,
    thresholds: Object.values(nextWorkflowGraphData.thresholds),
  });

  nextWorkflowGraphData.thresholds = {
    ...nextWorkflowGraphData.thresholds,
    ...indexThresholdsByPublicId(resolvedWorkflowThresholds),
    ...indexThresholdsByPublicId(resolvedSnippetThresholds),
  };

  const wfChildActionThresholds = indexThresholdsByPublicId(
    getThresholdsForActions({
      actions: Object.values(wfSnippetChildActions),
      thresholds: workflow.thresholds,
    }),
  );

  // compare workflow and snippet thresholds
  const thresholdDeltaMap = buildSnippetUpgradeDeltaMap({
    snippetElements: snippet.data.thresholds,
    workflowElements: wfChildActionThresholds,
  });

  // remove any thresholds that were removed in the incoming snippet version
  nextWorkflowGraphData.thresholds = indexThresholdsByPublicId(
    Object.values(nextWorkflowGraphData.thresholds)
      .filter(threshold => !thresholdDeltaMap.removed.has(threshold.publicId)),
  );

  // ==== DataRoutes =====
  /**
   * resolve all snippet dataRoutes to update their references to point
   * to any merged action.
   */
  const resolvedDataRoutes = resolveDataRouteSourceReferences({
    actionIdMap: externalActionMergeMap,
    dataRoutes: Object.values(snippet.data.dataRoutes),
  });


  /**
   * a workflow dataRoutes might point to a snippet action that gets merged during the update,
   * so those are resolved as well
   */
  const resolvedWorkflowDataRoutes = resolveDataRouteSourceReferences({
    actionIdMap: externalActionMergeMap,
    dataRoutes: Object.values(nextWorkflowGraphData.dataRoutes),
  });


  nextWorkflowGraphData.dataRoutes = {
    ...nextWorkflowGraphData.dataRoutes,
    ...indexDataRoutesByPublicId(resolvedWorkflowDataRoutes),
    ...indexDataRoutesByPublicId(resolvedDataRoutes),
  };

  const wfChildActionDataRoutes = indexDataRoutesByPublicId(
    getDataRoutesForActions({
      actions: Object.values(wfSnippetChildActions),
      dataRoutes: workflow.dataRoutes,
    }),
  );

  const dataRouteDeltaMap = buildSnippetUpgradeDeltaMap({
    snippetElements: snippet.data.dataRoutes,
    workflowElements: wfChildActionDataRoutes,
  });

  nextWorkflowGraphData.dataRoutes = indexDataRoutesByPublicId(
    Object.values(nextWorkflowGraphData.dataRoutes)
      .filter(dataRoute => !dataRouteDeltaMap.removed.has(dataRoute.publicId)),
  );

  return nextWorkflowGraphData;
}


/**
 * Maps incoming snippet external actions to their equivalent workflow actions.
 * Returns a map of snippetActionPublicId → workflowActionPublicId for each collision.
 */
export function computeExternalActionMergeMap(args: {
  workflow: {
    actions: Record<PublicId, Action>;
    thresholds: Record<PublicId, Threshold>;
    dataRoutes: Record<PublicId, DataRoute>;
  };
  snippet: SnippetVersion;
  snippetActionPublicId: PublicId;
}): Map<PublicId, PublicId> {
  const { workflow, snippet, snippetActionPublicId } = args;

  const actionMergeMap = new Map<PublicId, PublicId>();
  const externalWorkflowActions = Object
    .values(workflow.actions)
    .filter(action => isExternalActionType(action.type))
    // exclude external actions that belong to the snippet - otherwise a snippet
    // action would merge with itself
    .filter(action => !isChildOfSnippetAction({
      action,
      snippetParentActionPublicId: snippetActionPublicId,
    }));

  const workflowExternalActionsCollisionMap = getCollisionsWithinPrimitives({
    primitives: externalWorkflowActions,
  });

  for (const snippetAction of Object.values(snippet.data.actions)) {
    if (!isExternalSnippetChildAction(snippetAction)) continue;

    const equivalencyKey = getEquivalencyKeyForAction({ action: snippetAction });
    const collidingActionPublicIds = workflowExternalActionsCollisionMap.get(equivalencyKey);

    if ((collidingActionPublicIds?.size ?? 0) > 0) {
      // there will be at most 1 collision on external actions, so this is safe
      const [equivalentWorkflowActionPublicId] = collidingActionPublicIds.values();

      // mark the action as merged
      actionMergeMap.set(snippetAction.publicId, equivalentWorkflowActionPublicId);
    }
  }

  return actionMergeMap;
}


/**
 * Maps workflow actions that need to be unmerged from a snippet.
 * workflowActionId → snippetActionId (or undefined if the snippet action was removed).
 *
 * A WF action needs unmerging when:
 * - modify: the snippet action it was merged with is no longer equivalent
 * - remove: the snippet action it was merged with no longer exists in the new version
 */
export function computeExternalActionUnmergeMap(args: {
  workflow: {
    actions: Record<PublicId, Action>;
    thresholds: Record<PublicId, Threshold>;
    dataRoutes: Record<PublicId, DataRoute>;
  };
  snippet: SnippetVersion;
  parentSnippetActionPublicId: PublicId;
}): Map<PublicId, PublicId | undefined> {
  const { workflow, snippet, parentSnippetActionPublicId } = args;

  // WF actions that could be unmerge targets: external type, child of this snippet
  const externalWorkflowActions = Object
    .values(workflow.actions)
    .filter(supportsActionMerging)
    // only consider snippet child actions present in the workflow as they are the only
    // ones that an action can unmerge from
    .filter(action => isChildOfSnippetAction({
      action,
      snippetParentActionPublicId: parentSnippetActionPublicId,
    }));

  const snippetExternalActions = Object
    .values(snippet.data.actions)
    .filter(isExternalSnippetChildAction);

  const actionUnmergeMap = new Map<PublicId, PublicId | undefined>();

  for (const wfAction of externalWorkflowActions) {
    const mergedSnippetAction = snippetExternalActions.find(snippetAction => (
      isSourceActionMergedIntoTargetAction({
        snippetParentActionPublicId: parentSnippetActionPublicId,
        source: snippetAction,
        target: wfAction,
      })
    ));

    if (!mergedSnippetAction) {
      // remove: snippet action no longer exists in v2
      actionUnmergeMap.set(wfAction.publicId, undefined);
      continue;
    }

    const wfActionEqKey = getEquivalencyKeyForAction({ action: wfAction });
    const snippetActionEqKey = getEquivalencyKeyForAction({ action: mergedSnippetAction });

    // modify: snippet action still exists but is no longer equivalent
    if (wfActionEqKey !== snippetActionEqKey) {
      actionUnmergeMap.set(wfAction.publicId, mergedSnippetAction.publicId);
    }
  }

  return actionUnmergeMap;
}


export function prepareLinkedSnippetForUpgrade(args: {
  snippet: SnippetVersion;
  localToRemotePublicIds: Record<PublicId, PublicId>;
  parentSnippetActionPublicId: PublicId;
  localCurrentSnippetData: SnippetVersion['data'];
}): SnippetVersion {
  const {
    snippet, localToRemotePublicIds, parentSnippetActionPublicId, localCurrentSnippetData,
  } = args;

  const snippetWithContainerContexts = transferOrAttachContainerContexts({
    snippet,
    parentSnippetActionPublicId,
    localCurrentSnippetData,
    localToRemotePublicIds,
  });

  const snippetWithUpgradedParentAction = upgradeParentSnippetAction({
    snippet: snippetWithContainerContexts,
    parentSnippetActionPublicId,
    localCurrentSnippetData,
  });

  const snippetWithGateRules = transferOrAttachGateRules({
    localCurrentSnippetData,
    parentSnippetActionPublicId,
    snippet: snippetWithUpgradedParentAction,
  });

  const sortedSnippet = sortSnippetInTopologicalOrder({
    snippet: snippetWithGateRules,
  });

  return sortedSnippet;
}


function upgradeParentSnippetAction(args: {
  snippet: SnippetVersion;
  parentSnippetActionPublicId: PublicId;
  localCurrentSnippetData: SnippetVersion['data'];
}): SnippetVersion {
  const { snippet, parentSnippetActionPublicId, localCurrentSnippetData } = args;

  if (snippet.metadata === null) {
    throw new Error('Snippet must contain metadata');
  }

  const currentSnippetAction = localCurrentSnippetData.actions[parentSnippetActionPublicId];
  if (!currentSnippetAction) {
    throw new Error('Parent snippet action not found');
  }

  const nextLocalSnippetAction: Action = {
    ...currentSnippetAction,
    snippet: {
      name: currentSnippetAction.snippet.name,
      actionOptionPublicId: currentSnippetAction.snippet.actionOptionPublicId,
      token: snippet.metadata.token,
      // upgrade the public version to the new version
      publicVersion: snippet.metadata.version,
    },
  };

  const updatedActions = [...Object.values(snippet.data.actions), nextLocalSnippetAction];

  return createSnippetVersion({
    actions: indexActionsByPublicId(updatedActions),
    thresholds: snippet.data.thresholds,
    dataRoutes: snippet.data.dataRoutes,
    tags: snippet.props.tags,
    outcomes: snippet.props.outcomes,
    services: snippet.props.services,
    publishedAttributes: snippet.props.publishedAttributes,
    reasonCodeGroups: snippet.props.reasonCodeGroups,
    lists: snippet.props.lists,
    globalServiceConfigOptions: snippet.props.globalServiceConfigOptions,
    metadata: snippet.metadata,
    snippets: snippet.props.snippets,
  });
}


function transferOrAttachContainerContexts(args: {
  snippet: SnippetVersion;
  parentSnippetActionPublicId: PublicId;
  localCurrentSnippetData: SnippetVersion['data'];
  localToRemotePublicIds: Record<PublicId, PublicId>;
}): SnippetVersion {
  const {
    snippet, parentSnippetActionPublicId, localCurrentSnippetData, localToRemotePublicIds,
  } = args;

  const childActionsWithContainerContext = Object.values(snippet.data.actions)
    .map(snippetAction => {
      const localCurrentAction = localCurrentSnippetData.actions[snippetAction.publicId];

      if (localCurrentAction) {
        // if there's a local action, use its container context
        return {
          ...snippetAction,
          containerContext: {
            ...localCurrentAction.containerContext,
          },
        };
      }

      const remoteActionPublicId = localToRemotePublicIds[snippetAction.publicId];
      if (!remoteActionPublicId) {
        throw new Error(`could not resolve remote action public id: ${snippetAction.publicId}`);
      }
      return {
        ...snippetAction,
        containerContext: createSnippetContainerContext({
          parentSnippetActionPublicId,
          remoteActionPublicId,
        }),
      };
    });

  return createSnippetVersion({
    actions: indexActionsByPublicId(childActionsWithContainerContext),
    thresholds: snippet.data.thresholds,
    dataRoutes: snippet.data.dataRoutes,
    tags: snippet.props.tags,
    outcomes: snippet.props.outcomes,
    services: snippet.props.services,
    publishedAttributes: snippet.props.publishedAttributes,
    reasonCodeGroups: snippet.props.reasonCodeGroups,
    lists: snippet.props.lists,
    globalServiceConfigOptions: snippet.props.globalServiceConfigOptions,
    metadata: snippet.metadata,
    snippets: snippet.props.snippets,
  });
}


function transferOrAttachGateRules(args: {
  snippet: SnippetVersion;
  parentSnippetActionPublicId: PublicId;
  localCurrentSnippetData: SnippetVersion['data'];
}): SnippetVersion {
  const { snippet, parentSnippetActionPublicId, localCurrentSnippetData } = args;


  // array of entries with actions that had new rules and their gate threshold
  const actionGateRuleEntries: Array<[Action, Threshold]> = Object.values(snippet.data.actions)
    .flatMap(remoteAction => {
      if (!actionRequiresGateRule(remoteAction)) {
        return [];
      }

      const localAction = localCurrentSnippetData.actions[remoteAction.publicId];

      if (localAction) {
        const currentGateThresholdPublicId = getGateRuleId({
          actionId: localAction.publicId,
          containerId: parentSnippetActionPublicId,
          thresholds: localCurrentSnippetData.thresholds,
        });

        if (!currentGateThresholdPublicId) {
          throw new Error('local current action must already have a gate rule');
        }

        const updatedRules = addThresholdToBooleanRule({
          thresholdId: currentGateThresholdPublicId,
          pathToPerformActionAt: booleanRuleStrings.AND_OPERATOR_STRING,
          originalRule: remoteAction.rules ?? {},
        });

        if (!updatedRules) {
          throw new Error('Unable to add gate rule to action');
        }

        const actionWithTransferedGateRule = {
          ...remoteAction,
          rules: updatedRules,
        };

        const currentGateThreshold = (
          localCurrentSnippetData.thresholds[currentGateThresholdPublicId]);
        return [[actionWithTransferedGateRule, currentGateThreshold]];
      }


      const gateThreshold = createSnippetGateThreshold({
        ownerActionId: remoteAction.publicId,
        parentActionId: parentSnippetActionPublicId,
      });

      const updatedRules = addThresholdToBooleanRule({
        thresholdId: gateThreshold.publicId,
        pathToPerformActionAt: booleanRuleStrings.AND_OPERATOR_STRING,
        originalRule: remoteAction.rules ?? {},
      });

      if (!updatedRules) {
        throw new Error('Unable to add gate rule to action');
      }

      const actionWithNewGateRule = {
        ...remoteAction,
        rules: updatedRules,
      };


      return [[actionWithNewGateRule, gateThreshold]];
    });


  const updatedActions = {
    ...snippet.data.actions,
    ...indexActionsByPublicId(
      actionGateRuleEntries.map(([action]) => action),
    ),
  };

  const updatedThresholds = {
    ...snippet.data.thresholds,
    ...indexThresholdsByPublicId(
      actionGateRuleEntries.map(([, threshold]) => threshold),
    ),
  };

  return createSnippetVersion({
    actions: updatedActions,
    thresholds: updatedThresholds,
    dataRoutes: snippet.data.dataRoutes,
    tags: snippet.props.tags,
    outcomes: snippet.props.outcomes,
    services: snippet.props.services,
    publishedAttributes: snippet.props.publishedAttributes,
    reasonCodeGroups: snippet.props.reasonCodeGroups,
    lists: snippet.props.lists,
    globalServiceConfigOptions: snippet.props.globalServiceConfigOptions,
    metadata: snippet.metadata,
    snippets: snippet.props.snippets,
  });
}


function areActionsEqual(args: {
  workflowAction: Action;
  snippetAction: Action;
  localSnippetActionPublicId: PublicId;
}): boolean {
  const { workflowAction, snippetAction, localSnippetActionPublicId } = args;


  if (workflowAction.type !== snippetAction.type) {
    throw new Error('Actions types must match');
  }

  if (isExternalActionType(workflowAction.type)) {
    const workflowActionRemoteId = getRemoteActionPublicId({
      action: workflowAction,
      snippetParentActionPublicId: localSnippetActionPublicId,
    });

    const snippetActionRemoteId = getRemoteActionPublicId({
      action: snippetAction,
      snippetParentActionPublicId: localSnippetActionPublicId,
    });

    if (workflowActionRemoteId !== snippetActionRemoteId) {
      return false;
    }

    /**
     * INPUT is the only external action whose equivalency can change
     * without the snippet remote id changing.
     */
    if (isActionOfType(workflowAction, ActionType.INPUT)) {
      const wfEqKey = getEquivalencyKeyForAction({ action: workflowAction });
      const snEqKey = getEquivalencyKeyForAction({ action: snippetAction });
      return wfEqKey === snEqKey;
    }

    return true;
  }


  const prunedWorkflowAction = removeNonDeterministicActionFields(workflowAction);
  const prunedSnippetAction = removeNonDeterministicActionFields(snippetAction);

  return areEqual(prunedSnippetAction, prunedWorkflowAction);
}


export function buildActionsDeltaMap(args: {
  workflowActions: Record<PublicId, Action>;
  snippetActions: Record<PublicId, Action>;
  localSnippetActionPublicId: PublicId;
}): WFElementOperationMap {
  const { workflowActions, snippetActions, localSnippetActionPublicId } = args;

  const operationMap: WFElementOperationMap = {
    added: new Set(),
    modified: new Set(),
    removed: new Set(),
    noop: new Set(),
  };


  const allActionsIds = [
    ...Object.keys(workflowActions),
    ...Object.keys(snippetActions),
  ];


  for (const actionId of allActionsIds) {
    const workflowAction = workflowActions[actionId];
    const snippetAction = snippetActions[actionId];

    if (!workflowAction && snippetAction) {
      operationMap.added.add(actionId);
    }

    if (workflowAction && !snippetAction) {
      operationMap.removed.add(actionId);
    }

    if (workflowAction && snippetAction) {
      const actionsAreEqual = areActionsEqual({
        workflowAction,
        snippetAction,
        localSnippetActionPublicId,
      });

      if (!actionsAreEqual) {
        operationMap.modified.add(actionId);
      }

      if (actionsAreEqual) {
        operationMap.noop.add(actionId);
      }
    }
  }

  return operationMap;
}


export function buildSnippetUpgradeDeltaMap<T extends WFElement>(args: {
  workflowElements: Record<T['publicId'], T>;
  snippetElements: Record<T['publicId'], T>;
}): WFElementOperationMap {
  const { workflowElements, snippetElements } = args;

  const operationMap: WFElementOperationMap = {
    added: new Set(),
    modified: new Set(),
    removed: new Set(),
    noop: new Set(),
  };

  const allElements = [
    ...Object.values(snippetElements),
    ...Object.values(workflowElements),
  ] as T[];

  for (const element of allElements) {
    const { publicId: elementPublicId } = element;
    const wfElement = workflowElements[elementPublicId];
    const snElement = snippetElements[elementPublicId];

    // added
    if (!wfElement && snElement) {
      operationMap.added.add(elementPublicId);
    }

    // removed
    if (wfElement && !snElement) {
      operationMap.removed.add(elementPublicId);
    }

    // modified
    if (wfElement && snElement && areDifferent(wfElement, snElement)) {
      operationMap.modified.add(elementPublicId);
    }

    // noop
    if (wfElement && snElement && areEqual(wfElement, snElement)) {
      operationMap.noop.add(elementPublicId);
    }
  }

  return operationMap;
}
